import {
  RichTextEngine,
  settingGet,
  SettingName,
  SettingType,
  userSettingsGetOptions,
  userSettingUpdate,
} from "@scayla/api-client-fetch";
import { setComponentsLocale } from "@scayla/components";
import { setLocale as setCoreLocale } from "@scayla/core";
import { useQuery } from "@tanstack/vue-query";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { i18n } from "@/i18n";
import {
  generateDefaultPalette,
  generateSimplePalette,
  isRgbObject,
  isValidRgbValue,
  rgbToHex,
} from "@/utils/colorConverter";
import { showToastForErrorRef } from "@/utils/toast";

import { useWorkspace } from "./workspace";

interface SettingValues {
  [SettingName.UNDERLINE_VARIANT_VISIBILITY]: boolean;
  [SettingName.RECORD_EDIT_MODE]: boolean;
  [SettingName.ACCENT_COLOR]: object;
  [SettingName.COUNTRY_CODE]: string;
  [SettingName.HOME_TIMEZONE]: string;
  [SettingName.DATE_FORMAT]: string;
  [SettingName.TIME_FORMAT]: string;
  [SettingName.LANGUAGE]: string;
  [SettingName.IMPLICIT_RELATIONS_TAB_VIEW]: boolean;
  [SettingName.DEFAULT_TENANT]: string;
  [SettingName.NAVBAR_TYPE]: string;
  [SettingName.NAVBAR_VIEW]: string;
  [SettingName.HEADER_VIEW_MODE]: string;
  [SettingName.FAVORITES]: Record<string, boolean>;
  [SettingName.DECIMAL_SEPARATOR]: string;
  [SettingName.THOUSANDS_SEPARATOR]: string;
  [SettingName.DATE_FIELD_MONTHS_COUNT]: number;
  [SettingName.DATE_FIELD_MONTH_COLUMNS]: number;
  [SettingName.TENANT_ORG_TREE_EXPANDED]: Record<string, boolean>;
  [SettingName.RICH_TEXT_ENGINE]: string;
}

export const useUserSettingsStore = defineStore("userSettings", () => {
  const isRefreshing = ref(false);
  const workspace = useWorkspace();

  const builtQueryParams = (isDefault = false) =>
    computed(() => ({
      ...userSettingsGetOptions({
        query: {
          tenantId: workspace.tenantId,
          default: isDefault,
        },
      }),
      enabled: !!workspace.tenantId,
    }));

  const userQueryParams = builtQueryParams(false);

  const {
    data: userSettingsRaw,
    refetch: refetchUserSettings,
    isLoading: userSettingsLoading,
    error: userSettingsError,
  } = useQuery(userQueryParams);

  showToastForErrorRef(userSettingsError);

  const userSettingsMap = computed(() => {
    const map = new Map();
    (userSettingsRaw?.value?.items ?? []).forEach((s) => {
      map.set(s.name, s);
    });
    return map;
  });

  const defaultQueryParams = builtQueryParams(true);

  const {
    data: defaultSettingsRaw,
    refetch: refetchDefaultSettings,
    isLoading: defaultSettingsLoading,
    error: defaultSettingsError,
  } = useQuery(defaultQueryParams);

  showToastForErrorRef(defaultSettingsError);

  const defaultSettingsMap = computed(() => {
    const map = new Map();
    (defaultSettingsRaw?.value?.items ?? []).forEach((s) => {
      map.set(s.name, s);
    });
    return map;
  });

  const globalUserSettingsQueryParams = computed(() => ({
    ...userSettingsGetOptions({
      query: {},
    }),
    enabled: !!workspace.user?.id,
  }));

  const {
    data: globalUserSettingsRaw,
    refetch: refetchGlobalUserSettings,
    isLoading: globalUserSettingsLoading,
    error: globalUserSettingsError,
  } = useQuery(globalUserSettingsQueryParams);

  showToastForErrorRef(globalUserSettingsError);

  const globalUserSettingsMap = computed(() => {
    const map = new Map();
    (globalUserSettingsRaw?.value?.items ?? []).forEach((s) => {
      map.set(s.name, s);
    });
    return map;
  });

  function getSetting<T extends SettingName & keyof SettingValues>(key: T): SettingValues[T] | undefined {
    const setting = userSettingsMap.value.get(key);
    if (!setting) return undefined;

    const rawValue = setting.value;

    switch (key) {
      case SettingName.RECORD_EDIT_MODE:
        if (rawValue === true || rawValue === false) {
          return (rawValue === true) as SettingValues[T];
        } else {
          return undefined;
        }
    }

    switch (setting.type) {
      case SettingType.BOOLEAN:
        return (rawValue === true || rawValue === "true") as SettingValues[T];
      case SettingType.INTEGER:
        return Number(rawValue) as unknown as SettingValues[T];
      case SettingType.JSON:
        if (!rawValue) return {} as SettingValues[T];
        if (typeof rawValue === "object") return rawValue as SettingValues[T];

        try {
          const parsed = JSON.parse(rawValue as string);
          return parsed as SettingValues[T];
        } catch {
          console.warn("Failed to parse JSON setting:", key, rawValue);
          return {} as SettingValues[T];
        }
      case SettingType.STRING:
      default:
        return rawValue as unknown as SettingValues[T];
    }
  }

  const underlineVariantVisibility = computed(() => {
    return getSetting(SettingName.UNDERLINE_VARIANT_VISIBILITY) ?? false;
  });

  const recordEditMode = computed(() => {
    return getSetting(SettingName.RECORD_EDIT_MODE) ?? false;
  });

  /**
   * The user's rich text editor; `undefined` while the settings load, so no editor starts before the choice is known.
   * Anything but Tiptap, an unknown value or no settings at all (outside a tenant) included, keeps TinyMCE.
   */
  const richTextEngine = computed(() => {
    const value = getSetting(SettingName.RICH_TEXT_ENGINE);
    if (value === RichTextEngine.TIPTAP) return RichTextEngine.TIPTAP;
    if (value === undefined && userSettingsLoading.value) return undefined;
    return RichTextEngine.TINYMCE;
  });

  const accentColor = computed(() => {
    return getSetting(SettingName.ACCENT_COLOR) ?? { r: 17, g: 57, b: 151 };
  });

  const countryCode = computed(() => {
    return getSetting(SettingName.COUNTRY_CODE) ?? "CH";
  });

  const homeTimezone = computed(() => {
    return getSetting(SettingName.HOME_TIMEZONE) ?? "Europe/Zurich";
  });

  const timeFormat = computed(() => {
    return getSetting(SettingName.TIME_FORMAT) ?? "HH:mm";
  });

  const dateFormat = computed(() => {
    return getSetting(SettingName.DATE_FORMAT) ?? "dd.MM.yyyy";
  });

  const decimalSeparator = computed(() => {
    return getSetting(SettingName.DECIMAL_SEPARATOR) ?? "comma";
  });

  const thousandsSeparator = computed(() => {
    return getSetting(SettingName.THOUSANDS_SEPARATOR) ?? "apostrophe";
  });

  const dateFieldMonthsCount = computed(() => {
    return getSetting(SettingName.DATE_FIELD_MONTHS_COUNT) ?? 1;
  });

  const dateFieldMonthColumns = computed(() => {
    return getSetting(SettingName.DATE_FIELD_MONTH_COLUMNS) ?? 1;
  });

  const language = computed(() => {
    return getSetting(SettingName.LANGUAGE);
  });

  const implicitRelationsTabView = computed(() => {
    return getSetting(SettingName.IMPLICIT_RELATIONS_TAB_VIEW);
  });

  function getGlobalSetting<T extends SettingName & keyof SettingValues>(key: T): SettingValues[T] | undefined {
    const setting = globalUserSettingsMap.value.get(key);
    if (!setting) return undefined;

    const rawValue = setting.value;

    switch (setting.type) {
      case SettingType.BOOLEAN:
        return (rawValue === true || rawValue === "true") as SettingValues[T];
      case SettingType.INTEGER:
        return Number(rawValue) as unknown as SettingValues[T];
      case SettingType.JSON:
        if (!rawValue) return {} as SettingValues[T];
        if (typeof rawValue === "object") return rawValue as SettingValues[T];

        try {
          const parsed = JSON.parse(rawValue as string);
          return parsed as SettingValues[T];
        } catch {
          console.warn("Failed to parse JSON setting:", key, rawValue);
          return {} as SettingValues[T];
        }
      case SettingType.STRING:
      default:
        return rawValue as unknown as SettingValues[T];
    }
  }

  const defaultTenant = computed(() => {
    return getGlobalSetting(SettingName.DEFAULT_TENANT);
  });

  const navbarType = computed(() => {
    return getGlobalSetting(SettingName.NAVBAR_TYPE) ?? "default";
  });

  const navbarView = computed(() => {
    return getGlobalSetting(SettingName.NAVBAR_VIEW) ?? "navigation";
  });

  const headerViewMode = computed(() => {
    return getGlobalSetting(SettingName.HEADER_VIEW_MODE) ?? "text";
  });

  const favorites = computed(() => {
    return getGlobalSetting(SettingName.FAVORITES) ?? {};
  });

  const tenantOrgTreeExpanded = computed(() => {
    return getGlobalSetting(SettingName.TENANT_ORG_TREE_EXPANDED) ?? {};
  });

  const applyAccentColor = (customColor?: { r: number; g: number; b: number }) => {
    const colorSetting = customColor ?? getSetting(SettingName.ACCENT_COLOR);

    if (isRgbObject(colorSetting)) {
      const { r, g, b } = colorSetting;

      if (isValidRgbValue(r) && isValidRgbValue(g) && isValidRgbValue(b)) {
        const baseHex = rgbToHex(r, g, b);
        const palette = r === 17 && g === 57 && b === 151 ? generateDefaultPalette() : generateSimplePalette(baseHex);

        Object.entries(palette).forEach(([varName, colorHex]) => {
          document.documentElement.style.setProperty(varName, colorHex);
        });
      } else {
        console.log("Incorrect RGB values, using default.", colorSetting);
      }
    } else {
      console.log("Invalid RGB structure in colorSetting, using default.", colorSetting);
    }
  };

  watch(language, (lang) => {
    if (lang === undefined) return;
    (i18n.global.locale as { value: string }).value = lang;
    setCoreLocale(lang);
    setComponentsLocale(lang);
  });

  watch(accentColor, () => {
    applyAccentColor();
  });

  async function refreshSettings() {
    isRefreshing.value = true;
    try {
      await refetchUserSettings();
    } finally {
      isRefreshing.value = false;
    }
  }

  async function refreshDefaultSettings() {
    isRefreshing.value = true;
    try {
      await refetchDefaultSettings();
    } finally {
      isRefreshing.value = false;
    }
  }

  async function updateUserSetting(
    settingName: SettingName,
    value: unknown,
    options?: {
      cachedSettingId?: string | undefined;
      refetch?: () => Promise<unknown>;
      errorMessage?: string;
    },
  ): Promise<void> {
    let settingId: string | undefined = options?.cachedSettingId;

    if (!settingId) {
      const { data: setting } = await settingGet({
        path: {
          settingName: settingName,
        },
      });
      settingId = setting?.id;
    }

    if (!settingId) {
      throw new Error(options?.errorMessage ?? `Could not determine settingId for ${settingName}`);
    }

    try {
      await userSettingUpdate({
        body: {
          settingId: settingId,
          value: value,
          userId: workspace?.user?.id,
        },
        path: {
          settingName: settingName,
        },
      });

      if (options?.refetch) {
        await options.refetch();
      }
    } catch (error) {
      console.error(options?.errorMessage ?? `Error updating ${settingName}`, error);
      throw error;
    }
  }

  async function setDefaultTenant(tenantId: string): Promise<void> {
    const setting = globalUserSettingsMap.value.get(SettingName.DEFAULT_TENANT);
    await updateUserSetting(SettingName.DEFAULT_TENANT, tenantId, {
      cachedSettingId: setting?.settingId,
      refetch: refetchGlobalUserSettings,
      errorMessage: "Error updating default tenant",
    });
  }

  async function setNavbarType(navbarType: "default" | "mini"): Promise<void> {
    const setting = globalUserSettingsMap.value.get(SettingName.NAVBAR_TYPE);
    await updateUserSetting(SettingName.NAVBAR_TYPE, navbarType, {
      cachedSettingId: setting?.settingId,
      refetch: refetchGlobalUserSettings,
      errorMessage: "Error updating navbar type",
    });
  }

  async function setNavbarView(navbarView: "navigation" | "favorites"): Promise<void> {
    const setting = globalUserSettingsMap.value.get(SettingName.NAVBAR_VIEW);
    await updateUserSetting(SettingName.NAVBAR_VIEW, navbarView, {
      cachedSettingId: setting?.settingId,
      refetch: refetchGlobalUserSettings,
      errorMessage: "Error updating navbar view",
    });
  }

  async function setHeaderViewMode(headerViewMode: "text" | "symbol" | "symbolAndText"): Promise<void> {
    const setting = globalUserSettingsMap.value.get(SettingName.HEADER_VIEW_MODE);
    await updateUserSetting(SettingName.HEADER_VIEW_MODE, headerViewMode, {
      cachedSettingId: setting?.settingId,
      refetch: refetchGlobalUserSettings,
      errorMessage: "Error updating header view mode",
    });
  }

  async function setDateFieldMonthsCount(count: number): Promise<void> {
    const setting = userSettingsMap.value.get(SettingName.DATE_FIELD_MONTHS_COUNT);
    await updateUserSetting(SettingName.DATE_FIELD_MONTHS_COUNT, count, {
      cachedSettingId: setting?.settingId,
      refetch: refetchUserSettings,
      errorMessage: "Error updating date field months count",
    });
  }

  async function setDateFieldMonthColumns(columns: number): Promise<void> {
    const setting = userSettingsMap.value.get(SettingName.DATE_FIELD_MONTH_COLUMNS);
    await updateUserSetting(SettingName.DATE_FIELD_MONTH_COLUMNS, columns, {
      cachedSettingId: setting?.settingId,
      refetch: refetchUserSettings,
      errorMessage: "Error updating date field month columns",
    });
  }

  async function setFavorites(favorites: Record<string, boolean>): Promise<void> {
    const setting = globalUserSettingsMap.value.get(SettingName.FAVORITES);
    await updateUserSetting(SettingName.FAVORITES, favorites, {
      cachedSettingId: setting?.settingId,
      refetch: refetchGlobalUserSettings,
      errorMessage: "Error updating favorites",
    });
  }

  async function setTenantOrgTreeExpanded(expanded: Record<string, boolean>): Promise<void> {
    const setting = globalUserSettingsMap.value.get(SettingName.TENANT_ORG_TREE_EXPANDED);
    await updateUserSetting(SettingName.TENANT_ORG_TREE_EXPANDED, expanded, {
      cachedSettingId: setting?.settingId,
      refetch: refetchGlobalUserSettings,
      errorMessage: "Error updating tenant org tree expanded state",
    });
  }

  return {
    userSettings: computed(() => Array.from(userSettingsMap.value.values())),
    defaultSettings: computed(() => Array.from(defaultSettingsMap.value.values())),
    isLoading: computed(
      () =>
        userSettingsLoading.value ||
        isRefreshing.value ||
        defaultSettingsLoading.value ||
        globalUserSettingsLoading.value,
    ),
    globalUserSettingsLoading,
    language,
    getSetting,
    refreshSettings,
    refreshDefaultSettings,
    underlineVariantVisibility,
    recordEditMode,
    richTextEngine,
    accentColor,
    applyAccentColor,
    countryCode,
    homeTimezone,
    timeFormat,
    dateFormat,
    decimalSeparator,
    thousandsSeparator,
    dateFieldMonthsCount,
    dateFieldMonthColumns,
    setDateFieldMonthsCount,
    setDateFieldMonthColumns,
    implicitRelationsTabView,
    defaultTenant,
    setDefaultTenant,
    navbarType,
    navbarView,
    setNavbarType,
    setNavbarView,
    headerViewMode,
    setHeaderViewMode,
    favorites,
    setFavorites,
    tenantOrgTreeExpanded,
    setTenantOrgTreeExpanded,
  };
});
